"use client";

import { usePathname } from "next/navigation";

type Props = {
  signedIn: boolean;
  unreadMessages: number;
  unreadNotifs: number;
};

const ITEMS: Array<{
  key: string;
  label: string;
  href: string;
  /** Match function for highlighting the active tab. */
  match: (p: string) => boolean;
  /** Compact 22-px SVG icon. */
  icon: React.ReactNode;
  /** Tab requires sign-in. We still show the icon but route to /login. */
  authed?: boolean;
  badgeKey?: "messages" | "notifs";
}> = [
  {
    key: "browse",
    label: "Browse",
    href: "/search",
    match: (p) => p === "/" || p.startsWith("/search") || p.startsWith("/listings"),
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <circle cx="10" cy="10" r="6" />
        <path d="M14.5 14.5L19 19" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "stays",
    label: "Stays",
    href: "/trips",
    match: (p) => p.startsWith("/trips") || p.startsWith("/favorites"),
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <rect x="3" y="5" width="16" height="14" rx="1.5" />
        <path d="M3 9h16M7 3v4M15 3v4" strokeLinecap="round" />
      </svg>
    ),
    authed: true,
  },
  {
    key: "host",
    label: "Host",
    href: "/host",
    match: (p) => p.startsWith("/host"),
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <path d="M3 11l8-7 8 7v8.5H3z" strokeLinejoin="round" />
        <path d="M9 19v-5h4v5" />
      </svg>
    ),
    authed: true,
  },
  {
    key: "messages",
    label: "Messages",
    href: "/messages",
    match: (p) => p.startsWith("/messages"),
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <path d="M3 6c0-1.5 1.2-2.5 3-2.5h10c1.8 0 3 1 3 2.5v8c0 1.5-1.2 2.5-3 2.5H9.5L5 19v-4.5C3.5 14.3 3 13.5 3 12z" strokeLinejoin="round" />
      </svg>
    ),
    authed: true,
    badgeKey: "messages",
  },
  {
    key: "notifications",
    label: "Alerts",
    href: "/notifications",
    match: (p) => p.startsWith("/notifications"),
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <path d="M6 10c0-2.8 2.2-5 5-5s5 2.2 5 5v3l1.5 2h-13L6 13z" strokeLinejoin="round" />
        <path d="M9 17.5a2 2 0 004 0" />
      </svg>
    ),
    authed: true,
    badgeKey: "notifs",
  },
];

/**
 * Fixed mobile bottom nav: Browse / Stays / Host / Messages / Alerts.
 * Hidden on desktop (≥ 720px) via the .mobile-bottom-nav CSS rule. The
 * `usePathname` hook makes this a client component so the active tab can
 * be highlighted without a full reload.
 */
export default function BottomNav({ signedIn, unreadMessages, unreadNotifs }: Props) {
  const pathname = usePathname() ?? "/";
  return (
    <nav className="mobile-bottom-nav" aria-label="Primary">
      {ITEMS.map((item) => {
        const active = item.match(pathname);
        const href = item.authed && !signedIn ? `/login?next=${encodeURIComponent(item.href)}` : item.href;
        const badge =
          item.badgeKey === "messages"
            ? unreadMessages
            : item.badgeKey === "notifs"
              ? unreadNotifs
              : 0;
        return (
          <a
            key={item.key}
            href={href}
            className={`mbn-item${active ? " on" : ""}`}
            aria-current={active ? "page" : undefined}
          >
            <span className="mbn-icon">
              {item.icon}
              {badge > 0 ? (
                <span className="mbn-badge">{badge > 9 ? "9+" : badge}</span>
              ) : null}
            </span>
            <span className="mbn-label">{item.label}</span>
          </a>
        );
      })}
    </nav>
  );
}
